import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';
import { getOriginFromIframeSrc } from '@/utils/iframeAccessTokenBridge';
import AuthService from '@/services/auth/Auth.services';
import { showSnackbar } from '@/stores/redux/actions';
import { enumSnackbarStatusType } from '@/types/enums';

import { CCPR } from './customerCommunicationPrefrencesReport.classes';
import { CplToastLevel, isCplMessage } from './cpl.types';

const CPL_AUTH_REDIRECT_KEY = 'cpl_embed_auth_redirect_pending';

const getCplBaseUrl = (): string => {
    const envName = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase();

    if (envName === 'local') {
        return 'https://cplog-test.lcwaikiki.com';
    }
    if (['dev', 'test', 'preprod'].includes(envName)) {
        return 'https://cplog-test.lcwaikiki.com';
    }
    return 'https://cplog.lcwaikiki.com';
};

const buildCplEmbedUrl = (cplBaseUrl: string, parentOrigin: string): string => {
    const url = new URL('/', cplBaseUrl.replace(/\/$/, ''));
    url.searchParams.set('embed', '1');
    url.searchParams.set('parentOrigin', parentOrigin);
    return url.toString();
};

const mapCplToastLevelToSnackbarStatus = (
    level: CplToastLevel
):
    | enumSnackbarStatusType.success
    | enumSnackbarStatusType.warning
    | enumSnackbarStatusType.danger
    | enumSnackbarStatusType.info => {
    switch (level) {
        case 'success':
            return enumSnackbarStatusType.success;
        case 'warning':
            return enumSnackbarStatusType.warning;
        case 'error':
            return enumSnackbarStatusType.danger;
        case 'info':
        default:
            return enumSnackbarStatusType.info;
    }
};

const hasParentAccessToken = (): boolean => Boolean(localStorage.getItem('access_token'));

export const CustomerCommunicationPrefrencesReport = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const authService = useMemo(() => new AuthService(), []);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const loginAttemptedRef = useRef(false);
    const [embedUnavailable, setEmbedUnavailable] = useState(false);

    const cplBaseUrl = useMemo(() => getCplBaseUrl(), []);
    const iframeSrc = useMemo(() => buildCplEmbedUrl(cplBaseUrl, window.location.origin), [cplBaseUrl]);
    const cplOrigin = useMemo(() => getOriginFromIframeSrc(iframeSrc), [iframeSrc]);

    const openCplInNewTab = useCallback(() => {
        window.open(cplBaseUrl, '_blank', 'noopener,noreferrer');
    }, [cplBaseUrl]);

    const handleCplAuthRequired = useCallback(
        (reason: string) => {
            if (hasParentAccessToken()) {
                // Parent is authenticated but the iframe cannot share Keycloak SSO cookies
                // (common in incognito / strict third-party cookie modes).
                console.warn('CPL auth required while parent session exists:', reason);
                setEmbedUnavailable(true);
                return;
            }

            if (loginAttemptedRef.current || sessionStorage.getItem(CPL_AUTH_REDIRECT_KEY)) {
                console.warn('CPL auth required but login redirect already attempted:', reason);
                dispatch(
                    showSnackbar({
                        message: t('general.sessionExpiredError'),
                        status: enumSnackbarStatusType.warning,
                    })
                );
                return;
            }

            loginAttemptedRef.current = true;
            sessionStorage.setItem(CPL_AUTH_REDIRECT_KEY, '1');
            console.warn('CPL auth required, redirecting parent to login:', reason);
            authService.login();
        },
        [authService, dispatch, t]
    );

    useEffect(() => {
        sessionStorage.removeItem(CPL_AUTH_REDIRECT_KEY);
    }, []);

    useEffect(() => {
        if (!cplOrigin) {
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }
            if (event.origin !== cplOrigin) {
                return;
            }
            if (!isCplMessage(event.data)) {
                return;
            }

            switch (event.data.type) {
                case 'cpl:auth-required':
                    handleCplAuthRequired(event.data.reason);
                    break;
                case 'cpl:toast':
                    dispatch(
                        showSnackbar({
                            message: event.data.message,
                            status: mapCplToastLevelToSnackbarStatus(event.data.level),
                        })
                    );
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [cplOrigin, dispatch, handleCplAuthRequired]);

    return (
        <SCUIContent className={CCPR.page}>
            <Box
                className={CCPR.frameCard}
                sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 'calc(100vh - 160px)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                {embedUnavailable ? (
                    <Box sx={{ p: 3 }}>
                        <Alert
                            className={CCPR.fallbackAlert}
                            severity="warning"
                            sx={{ alignItems: 'flex-start' }}
                            action={
                                <Button
                                    className={CCPR.openInNewTabButton}
                                    color="inherit"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewRoundedIcon />}
                                    onClick={openCplInNewTab}
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    {t('customerCommunicationPrefrencesReport.openInNewTab')}
                                </Button>
                            }
                        >
                            {t('customerCommunicationPrefrencesReport.privateBrowsingHint')}
                        </Alert>
                    </Box>
                ) : (
                    <Box
                        ref={iframeRef}
                        className={CCPR.iframe}
                        component="iframe"
                        title="Customer Preference Log"
                        src={iframeSrc}
                        sandbox="allow-scripts allow-same-origin allow-downloads allow-forms allow-popups"
                        sx={{ width: '100%', height: '725px', border: 0 }}
                    />
                )}
            </Box>
        </SCUIContent>
    );
};
